import { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import { Link as RouterLink, Navigate, useSearchParams } from 'react-router';
import { api } from '@/api';
import type { Personas } from '@/api/types';
import { getAuthErrorMessage, useAuth } from '@/auth';
import { SplashScreen } from '@/components/SplashScreen';
import { StatusScreen } from '@/components/StatusScreen';

function safeNext(value: string | null) {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/';
}

/**
 * Landing page after Google: trades the one-time `code` for a session on this
 * host, then continues to where the person was going.
 */
export function AuthCompletePage() {
  const [params] = useSearchParams();
  const { completeRedirectSignIn } = useAuth();
  const [state, setState] = useState<'working' | 'done' | { error: unknown }>('working');
  const started = useRef(false);
  const code = params.get('code');
  // Present when the mobile app opened this page after Google (see mobile/).
  const verifier = params.get('verifier') ?? undefined;
  const [next, setNext] = useState(() => safeNext(params.get('next')));

  useEffect(() => {
    if (started.current || !code) return;
    started.current = true;
    // Remove the code from the address bar and history right away.
    window.history.replaceState(null, '', '/auth/complete');
    completeRedirectSignIn(code, verifier).then(
      async () => {
        // Test accounts choose which profile to use right after signing in.
        if (next === '/') {
          const personas = await api.get<Personas>('/me/personas').catch(() => null);
          if (personas?.enabled) setNext('/perfis');
        }
        setState('done');
      },
      (error: unknown) => setState({ error }),
    );
  }, [code, verifier, next, completeRedirectSignIn]);

  if (!code && state === 'working') return <Navigate to="/login" replace />;
  if (state === 'done') return <Navigate to={next} replace />;
  if (state === 'working') return <SplashScreen />;
  return (
    <StatusScreen
      icon={<ErrorOutlineRounded color="error" sx={{ fontSize: 48 }} />}
      title="Não foi possível concluir o login"
      description={getAuthErrorMessage(state.error)}
      actions={
        <Button component={RouterLink} to="/login" variant="contained">
          Tentar de novo
        </Button>
      }
    />
  );
}
